import React, { useEffect, useRef } from 'react';
import { Building2, Factory, Home as HomeIcon, Battery, Sun, Zap, Users, Award, AlertTriangle, Thermometer, Plug, Wind, Car, CircleDollarSign, Settings as SettingsIcon, Shield, TrendingUp } from 'lucide-react';

export const ICON_MAP = { Sun, Zap, Battery, Thermometer, Plug, Wind, Car, CircleDollarSign, Settings: SettingsIcon, Building2, Factory, HomeIcon, Shield, TrendingUp, AlertTriangle, Award, Users };

import { API, BACKEND } from '@/utils/apiConfig';

export const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads/')) return `${BACKEND}/api${url}`;
  if (url.startsWith('/api/uploads/')) return `${BACKEND}${url}`;
  return url;
};

export const CategoryIcon = ({ iconName, color, size = 16 }) => {
  const Icon = ICON_MAP[iconName];
  return Icon ? <Icon size={size} color={color} /> : <Sun size={size} color={color} />;
};

export const DynIcon = ({ name, className }) => {
  const I = ICON_MAP[name] || Zap;
  return <I className={className} />;
};

const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { node.classList.add('visible'); observer.unobserve(node); } },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return ref;
};

export const RevealSection = ({ children, className = '', direction = 'up' }) => {
  const ref = useScrollReveal();
  const cls = direction === 'left' ? 'reveal-left' : direction === 'right' ? 'reveal-right' : direction === 'scale' ? 'reveal-scale' : 'reveal';
  return <div ref={ref} className={`${cls} ${className}`}>{children}</div>;
};

export const StaggerGrid = ({ children, className = '' }) => {
  const ref = useScrollReveal();
  return <div ref={ref} className={`stagger-children ${className}`}>{children}</div>;
};

export const getServiceIcon = (iconName) => {
  const icons = { Building2, Factory, HomeIcon, Battery, Sun, Zap };
  const Icon = icons[iconName] || Sun;
  return <Icon className="w-7 h-7" />;
};

export const getDefaultImage = (category) => {
  const categoryImages = {
    'fotovoltaika': 'https://images.unsplash.com/photo-1720610784599-18c02b1cc9ee?w=1400&q=75&auto=format&fit=crop',
    'fve-rodinny': 'https://images.unsplash.com/photo-1720610784599-18c02b1cc9ee?w=1400&q=75&auto=format&fit=crop',
    'fve-stredni': 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1400&q=75&auto=format&fit=crop',
    'fve-komercni': 'https://images.unsplash.com/photo-1624397640148-949b1732bb0a?w=1400&q=75&auto=format&fit=crop',
    'elektroinstalace': 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&auto=format&fit=crop',
    'tepelna_cerpadla': 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800&auto=format&fit=crop',
    'kamery': 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&auto=format&fit=crop',
    'zabezpeceni': 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&auto=format&fit=crop',
    'chytre_domy': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop',
    'carporty': 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&auto=format&fit=crop'
  };
  return categoryImages[category] || categoryImages['fotovoltaika'];
};

export const getManufacturerColor = (manufacturer) => {
  const colors = {
    'SOLAX': 'linear-gradient(135deg, #e63946, #d62828)',
    'GOODWE': 'linear-gradient(135deg, #2196f3, #1976d2)',
    'SOLAREDGE': 'linear-gradient(135deg, #ff6b00, #e65100)',
    'CANADIAN SOLAR': 'linear-gradient(135deg, #dc143c, #b91030)',
    'LONGI': 'linear-gradient(135deg, #00a86b, #008f5b)',
    'TRINA': 'linear-gradient(135deg, #1e88e5, #1565c0)',
    'SMA': 'linear-gradient(135deg, #ff5722, #e64a19)',
    'AIKO': 'linear-gradient(135deg, #7c4dff, #651fff)',
    'HIKVISION': 'linear-gradient(135deg, #d32f2f, #b71c1c)',
    'JABLOTRON': 'linear-gradient(135deg, #388e3c, #2e7d32)',
    'LOXONE': 'linear-gradient(135deg, #4caf50, #388e3c)',
    'SAMSUNG': 'linear-gradient(135deg, #1428a0, #0d1b6e)',
    'SOLARDOCK': 'linear-gradient(135deg, #16a34a, #15803d)',
    'SOLAR GREEN': 'linear-gradient(135deg, #16a34a, #15803d)'
  };
  return colors[manufacturer?.toUpperCase()] || 'linear-gradient(135deg, #475569, #334155)';
};
